import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { connect } from 'react-redux'
import styled from 'styled-components'
import moment from 'moment'
import arraySort from 'array-sort'
import { push } from 'react-router-redux'
import ReactTooltip from 'react-tooltip'
import colors from '../../../configuration/colors'
import withApiData from '../../HOC/withApiData'
import {
  fetchActivityCourseJourney,
  fetchActivityCourse
} from '../Journeys/API'
import Button from '../../mini/Buttons/Button'
import { showModal } from '../../../actions/modal'
import { convertToRomanNumerals } from '../../../utils/convertToRomanNumerals'
import MarkdownLabel from '../../mini/MarkdownLabel'
import NotYetSpecified from '../../mini/NotYetSpecified'
import { fetchJourneyLevels } from '../Levels/API'
import LevelRow from '../Levels/LevelRow'
import H3 from '../../typography/headers/H3'
import Caption1 from '../../typography/captions/Caption1'
import { cleanHexCode } from '../util'
import ResponsiveContainer from '../../mini/Layout/ResponsiveContainer'
import BackButton from '../../mini/MyCourses/BackButton'
import GradeElement from '../../mini/GradeElement'

const FlexContainer = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.h1`
  color: ${props => props.color};
  font-family: Montserrat, sans-serif;
  font-size: 34px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 45.6px;
  margin-right: 14px;
  margin-top: 0;
`

const Description = styled.h3`
  color: ${colors.dove};
  font-size: 11px;
  letter-spacing: 0;
  line-height: 16px;
  margin-bottom: 8px;
`

const JourneyDescriptionText = styled.p`
  color: ${props => (props.hasDescription ? colors.charcoal4 : colors.silver)};
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 19px;
  margin-bottom: 33px;
  max-width: 70%;
`

const ClickableDescriptionSection = styled.div`
  max-width: 800px;
  cursor: pointer;

  label {
    cursor: pointer;
  }
`

export const JourneyDetails = ({ apiData, activityCourseId, journeyId }) => {
  const dispatch = useDispatch()
  const gradeLevels = useSelector(state => state.gradelevels.items)

  const [isHovered, setIsHovered] = useState(false)
  const mouseOver = () => {
    setIsHovered(true)
  }

  const mouseOut = () => {
    setIsHovered(false)
  }

  const journey = useQuery(
    ['activity-course-journey-' + journeyId],
    () =>
      fetchActivityCourseJourney(
        apiData.orgId,
        apiData.token,
        activityCourseId,
        journeyId
      ),
    {
      refetchOnWindowFocus: false
    }
  )

  const activityCourse = useQuery(
    ['activity-course-' + activityCourseId],
    () => fetchActivityCourse(apiData.orgId, apiData.token, activityCourseId),
    {
      refetchOnWindowFocus: false
    }
  )

  const { isLoading: isLevelsLoading, data: levels } = useQuery(
    ['journey-levels-' + journeyId],
    () => fetchJourneyLevels(apiData, activityCourseId, journeyId),
    {
      refetchOnWindowFocus: false,
      initialData: []
    }
  )

  const [gradeLevelAbbreviation, setGradeLevelAbbreviation] = useState('')
  const [gradeName, setGradeName] = useState('')

  useEffect(() => {
    if (!activityCourse.isLoading && !!activityCourse.data) {
      const gl = gradeLevels.find(
        gl => gl.id === activityCourse.data.grade_level_id
      )
      setGradeLevelAbbreviation(gl.abbreviation)
      setGradeName(gl.name)
    }
  }, [journey, gradeLevelAbbreviation])

  const handleOpenEditModal = () => {
    dispatch(
      showModal('EDIT_JOURNEY_MODAL', {
        apiData,
        activityCourseId,
        journeyId
      })
    )
  }

  const handleLevelCreationModal = () => {
    dispatch(
      showModal('CREATE_OR_EDIT_LEVEL_MODAL', {
        activityCourseId,
        journeyId,
        isCreateLevel: true
      })
    )
  }

  const listOfLevels =
    !isLevelsLoading &&
    arraySort(levels, 'sort_order').map((level, index) => {
      return (
        <LevelRow
          key={`level-${level.id}`}
          activityCourseId={activityCourseId}
          journeyId={!!journey.data && journey.data.id}
          isReadyForEnrollment={
            !!journey.data && !!journey.data.marked_ready_for_enrollment_at
          }
          level={level}
          color={
            !!activityCourse.data && cleanHexCode(activityCourse.data.color)
          }
          levels={levels}
          courseId={activityCourseId}
          sequenceNumber={index + 1}
        />
      )
    })

  const levelWithoutQuests = levels.find(l => l.quests_count === 0)
  const questsCount = levels.map(l => l.quests_count).reduce((a, b) => a + b, 0)
  const questsInDraftCount = levels
    .map(l => l.quests_in_draft_count)
    .reduce((a, b) => a + b, 0)

  const isJourneyPublishable =
    !levelWithoutQuests && questsCount > 0 && questsInDraftCount === 0

  return (
    <ResponsiveContainer>
      {journey.isLoading ||
      activityCourse.isLoading ||
      gradeLevelAbbreviation === '' ? (
        <div className="loading" />
      ) : (
        <React.Fragment>
          <BackButton
            backToWhere="Courses"
            onClick={() => dispatch(push('/mycourses'))}
          />
          <div>
            <FlexContainer style={{
              alignItems: 'flex-start',
              marginTop: '55px'
            }}>
              <div style={{ width: '70%' }}>
                <FlexContainer
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    cursor: 'pointer'
                  }}
                  onClick={handleOpenEditModal}
                  onMouseOver={mouseOver}
                  onMouseOut={mouseOut}
                >
                  <GradeElement large
                    name={gradeName}
                    abbreviation={gradeLevelAbbreviation}
                    style={{ marginRight: '14px' }}
                  />
                  <Title color={cleanHexCode(activityCourse.data.color)}>
                    {activityCourse.data.abbreviation +
                      ': ' +
                      convertToRomanNumerals(
                        journey.data.master_course_course.index + 1
                      ) +
                      ' ' +
                      journey.data.name}
                  </Title>
                  <div style={{ marginTop: '2%' }}>
                    <svg
                      width="18"
                      height="18"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.829 3.6l2.7 2.7L4.114 16.714 0 18l1.414-3.986L11.83 3.6zM17.42.579c.772.771.772 1.928 0 2.571l-1.414 1.414-2.571-2.571L14.85.579c.771-.772 1.929-.772 2.571 0z"
                        fill={isHovered ? colors.cobalt : colors.aluminum}
                        fillRule="nonzero"
                      />
                    </svg>
                  </div>
                </FlexContainer>
              </div>
              {!journey.data.marked_ready_for_enrollment_at ? (
                <div
                  data-for="publishJourneyTooltip"
                  data-tip={
                    !isLevelsLoading && levels.length > 0
                      ? 'Please publish all activities prior to publishing this journey.'
                      : 'At least one level and one activity are required prior to publishing.'
                  }
                >
                  <Button
                    width="184px"
                    buttonColor={colors.oeYellow}
                    borderColor={colors.oeYellow}
                    hoverBackground={colors.charcoal4}
                    hoverColor={colors.white}
                    title="Publish Journey"
                    couldSaveData={isJourneyPublishable}
                    shouldShowTooltipAndDisableButton={!isJourneyPublishable}
                    handleClick={
                      isJourneyPublishable
                        ? () => {
                            dispatch(
                              showModal('PUBLISH_JOURNEY_MODAL', {
                                activityCourseId,
                                journeyId
                              })
                            )
                          }
                        : null
                    }
                  />
                  {!isJourneyPublishable && (
                    <ReactTooltip
                      id="publishJourneyTooltip"
                      place="bottom"
                      effect="solid"
                      className="activityCourseTooltip"
                    />
                  )}
                </div>
              ) : (
                <FlexContainer
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}
                >
                  <H3 style={{ color: colors.jade }}>Published</H3>
                  <Caption1>
                    {moment
                      .unix(journey.data.marked_ready_for_enrollment_at)
                      .format('DD MMMM YYYY')
                      .toString()}
                  </Caption1>
                </FlexContainer>
              )}
            </FlexContainer>
            <div>
              <ClickableDescriptionSection
                onClick={handleOpenEditModal}
                onMouseOver={mouseOver}
                onMouseOut={mouseOut}
              >
                <Description>Journey Description</Description>
                <div className="view-fields">
                  {journey.data.description.length === 0 ? (
                    <JourneyDescriptionText
                      hasDescription={journey.data.description.length > 0}
                    >
                      <NotYetSpecified />
                    </JourneyDescriptionText>
                  ) : (
                    <MarkdownLabel
                      value={journey.data.description}
                      additionalCssClasses="view-journey-description"
                    />
                  )}
                </div>
              </ClickableDescriptionSection>
              <Description>Levels / Activities</Description>
              <div>{listOfLevels}</div>
              {!!journey.data &&
                !journey.data.marked_ready_for_enrollment_at && (
                  <div style={{ marginTop: '15px' }}>
                    <Button
                      handleClick={handleLevelCreationModal}
                      width="136px"
                      title="+ Add Level"
                      buttonColor={colors.smoke}
                      borderColor={colors.aluminum}
                      hoverBackground={colors.charcoal4}
                      hoverColor={colors.white}
                    />
                  </div>
                )}
            </div>
          </div>
        </React.Fragment>
      )}
    </ResponsiveContainer>
  )
}

JourneyDetails.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.string.isRequired,
  journeyId: PropTypes.string.isRequired
}

export const mapStateToProps = (state, ownProps) => ({
  activityCourseId: ownProps.params.courseId,
  journeyId: ownProps.params.journeyId
})

export default withApiData(connect(mapStateToProps)(JourneyDetails))



// WEBPACK FOOTER //
// ./src/components/mycourses/Journey/JourneyDetails.js